/* eslint-disable */
import React, { useState, useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Button,
    useMediaQuery,
    CircularProgress,
    Box,
    Typography,
    IconButton,
    Alert,
    Switch,
    FormControlLabel,
    Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTravelRequest } from 'hooks/api/travel-request/useTravelRequest';
import { useBudgetLine } from 'hooks/api/terminology/budget-line/useBudgetLine';
import { useTravellerSearch } from 'hooks/api/travel-request/useTravellerSearch';
import { logger } from 'utils/environment/log';
import Swal from 'sweetalert2';
import {
    FlightTakeoff,
    FlightLand,
    Category,
    Person,
    AccountBalance,
    Business,
    Assignment,
    AccountBalanceWallet,
    AttachFile,
    Today,
    CalendarToday,
    ArrowBack,
    Send,
    InsertDriveFile,
    Check,
    Clear
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

// Import components
import FileUploadSection from './components/FileUploadSection';
import TripDetailsSection from './components/TripDetailsSection';
import TravelerSelectionSection from './components/TravelerSelectionSection';
import BudgetCodesSection from './components/BudgetCodesSection';
import DailyAllowanceMatrix from './components/DailyAllowanceMatrix';

// Import constants and validation schema
import { MAX_FILE_SIZE, ACCEPTED_FILE_TYPES, formatDateTimeForInput, formatDateTimeForSubmission } from './constants';
import { createTravelRequestSchema } from './validationSchema';

// Define allowance rates
const ALLOWANCE_RATES = {
    'Breakfast': 5,
    'Lunch': 15,
    'Dinner': 25,
    'Incidental': 5,
    'Proved Acc': 100,
    'Unproved Acc': 45,
    'No Acc Required': 0
};

// Preview Dialog Component
const PreviewDialog = ({ open, onClose, formData, files, onSubmit, isSubmitting }) => {
    const theme = useTheme();
    const coreColor = '#213363';
    const lightCoreColor = alpha(coreColor, 0.1);
    const mediumCoreColor = alpha(coreColor, 0.5);

    if (!formData) {
        return null;
    }

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'Not specified';
        try {
            return new Date(dateTime).toLocaleString();
        } catch (e) {
            return dateTime;
        }
    };



    const SectionTitle = ({ icon: Icon, title }) => (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            pb: 1.5,
            position: 'relative',
            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '2px',
                background: `linear-gradient(90deg, ${coreColor} 0%, ${mediumCoreColor} 50%, transparent 100%)`
            }
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '12px',
                backgroundColor: lightCoreColor,
                mr: 2
            }}>
                <Icon sx={{ color: coreColor }} />
            </Box>
            <Typography variant="h6" fontWeight="600" sx={{ color: coreColor }}>
                {title}
            </Typography>
        </Box>
    );

    const InfoItem = ({ label, value, icon: Icon }) => (
        <Box sx={{ 
            mb: 2,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(coreColor, 0.1)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
                backgroundColor: lightCoreColor,
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(coreColor, 0.1)}`
            }
        }}>
            <Typography variant="body2" gutterBottom sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: mediumCoreColor,
                mb: 1
            }}>
                {Icon && <Icon fontSize="small" sx={{ color: coreColor }} />}
                {label}
            </Typography>
            <Typography variant="body1" sx={{ 
                fontWeight: 500,
                color: coreColor
            }}>
                {value}
            </Typography>
        </Box>
    );

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="lg" 
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: `0 8px 32px ${alpha(coreColor, 0.2)}`
                }
            }}
        >
            <DialogTitle sx={{
                bgcolor: coreColor,
                color: 'white',
                p: 4
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" fontWeight="700" sx={{ mb: 1, color: 'white' }}>
                            Travel Request Preview
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Please review your request details before final submission
                        </Typography>
                    </Box>
                    <IconButton 
                        onClick={onClose} 
                        size="large" 
                        sx={{ 
                            color: 'white',
                            '&:hover': {
                                backgroundColor: alpha('#fff', 0.1)
                            }
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ 
                    p: 4,
                    maxHeight: '75vh',
                    overflowY: 'auto',
                    backgroundColor: alpha(coreColor, 0.02),
                    '&::-webkit-scrollbar': {
                        width: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: alpha(coreColor, 0.05)
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: mediumCoreColor,
                        borderRadius: '4px'
                    }
                }}>
                    <Grid container spacing={4}>
                        {/* Trip Details */}
                        <Grid item xs={12}>
                            <Paper elevation={0} sx={{ 
                                p: 4, 
                                borderRadius: 3,
                                backgroundColor: 'white',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: `0 8px 24px ${alpha(coreColor, 0.1)}`
                                }
                            }}>
                                <SectionTitle icon={FlightTakeoff} title="Trip Details" />
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <InfoItem 
                                            label="Travel Type"
                                            value={formData.travelType || 'Not specified'}
                                            icon={Category}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <InfoItem 
                                            label="Departure"
                                            value={formatDateTime(formData.departureDatetime)}
                                            icon={FlightTakeoff}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <InfoItem 
                                            label="Return"
                                            value={formatDateTime(formData.returnDatetime)}
                                            icon={FlightLand}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Daily Allowances */}
                        {formData.adjustDayToDay && formData.dailyAllowances && Object.keys(formData.dailyAllowances).length > 0 && (
                            <Grid item xs={12}>
                                <Paper elevation={0} sx={{ 
                                    p: 4, 
                                    borderRadius: 3,
                                    backgroundColor: 'white',
                                    transition: 'all 0.3s ease',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    '&:hover': {
                                        boxShadow: `0 8px 24px ${alpha(coreColor, 0.1)}`
                                    }
                                }}>
                                    <SectionTitle icon={Today} title="Daily Allowances Matrix" />
                                    
                                    {/* Calculate total allowances */}
                                    {(() => {
                                        let totalAmount = 0;
                                        let totalDays = Object.keys(formData.dailyAllowances).length;
                                        
                                        Object.entries(formData.dailyAllowances).forEach(([date, dayAllowances]) => {
                                            let dayTotal = 0;
                                            // Sum up all checked allowances
                                            Object.entries(dayAllowances).forEach(([type, value]) => {
                                                if (type !== 'miscAmount' && type !== 'venue' && value === true) {
                                                    dayTotal += ALLOWANCE_RATES[type] || 0;
                                                }
                                            });
                                            // Add misc amount if any
                                            if (dayAllowances.miscAmount) {
                                                dayTotal += parseFloat(dayAllowances.miscAmount) || 0;
                                            }
                                            totalAmount += dayTotal;
                                        });

                                        return (
                                            <Box sx={{ 
                                                mt: 3,
                                                p: 2,
                                                bgcolor: alpha(coreColor, 0.05),
                                                borderRadius: 2,
                                                border: `1px solid ${alpha(coreColor, 0.1)}`
                                            }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={4}>
                                                        <Box sx={{ 
                                                            p: 2, 
                                                            bgcolor: 'white', 
                                                            borderRadius: 1,
                                                            border: `1px solid ${alpha(coreColor, 0.1)}`
                                                        }}>
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                Total Days
                                                            </Typography>
                                                            <Typography variant="h5" sx={{ color: coreColor, fontWeight: 600 }}>
                                                                {totalDays}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <Box sx={{ 
                                                            p: 2, 
                                                            bgcolor: 'white', 
                                                            borderRadius: 1,
                                                            border: `1px solid ${alpha(coreColor, 0.1)}`
                                                        }}>
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                Average Per Day
                                                            </Typography>
                                                            <Typography variant="h5" sx={{ color: coreColor, fontWeight: 600 }}>
                                                                ${(totalAmount / totalDays).toFixed(2)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <Box sx={{ 
                                                            p: 2, 
                                                            bgcolor: coreColor,
                                                            borderRadius: 1,
                                                            boxShadow: `0 4px 12px ${alpha(coreColor, 0.2)}`
                                                        }}>
                                                            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                                Total Amount
                                                            </Typography>
                                                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                                                                ${totalAmount.toFixed(2)}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        );
                                    })()}
                                    
                                    {/* Matrix Container */}
                                    <Box sx={{ 
                                        mt: 2,
                                        border: `1px solid ${alpha(coreColor, 0.1)}`,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        position: 'relative',
                                        height: 'auto',
                                        maxHeight: '600px',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        {/* Sticky Header */}
                                        <Box sx={{ 
                                            bgcolor: alpha(coreColor, 0.02),
                                            borderBottom: `1px solid ${alpha(coreColor, 0.1)}`,
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            display: 'flex',
                                            width: '100%'
                                        }}>
                                            <Box sx={{ 
                                                width: '200px',
                                                p: 2,
                                                borderRight: `1px solid ${alpha(coreColor, 0.1)}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'white'
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    color: coreColor
                                                }}>
                                                    <CalendarToday sx={{ fontSize: 20 }} />
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        Travel Dates
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ 
                                                display: 'flex',
                                                flex: 1,
                                                overflowX: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    height: '8px'
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    backgroundColor: alpha(coreColor, 0.05)
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: alpha(coreColor, 0.3),
                                                    borderRadius: '4px',
                                                    '&:hover': {
                                                        backgroundColor: alpha(coreColor, 0.4)
                                                    }
                                                }
                                            }}>
                                                {Object.entries(Object.values(formData.dailyAllowances)[0] || {}).map(([allowanceType], index) => {
                                                    // Skip these fields in the preview
                                                    if (['provinceId', 'districtId', 'venueId', 'venueType'].includes(allowanceType)) {
                                                        return null;
                                                    }
                                                    return (
                                                        <Box 
                                                            key={allowanceType}
                                                            sx={{ 
                                                                minWidth: allowanceType === 'venue' ? '250px' : '150px',
                                                                p: 2,
                                                                borderRight: `1px solid ${alpha(coreColor, 0.1)}`,
                                                                textAlign: 'center',
                                                                bgcolor: 'white'
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    {allowanceType === 'venue' ? 'Venue' : allowanceType}
                                                                </Typography>
                                                                {allowanceType !== 'venue' && allowanceType !== 'miscAmount' && (
                                                                    <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                                                        (${ALLOWANCE_RATES[allowanceType]})
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>

                                        {/* Scrollable Content */}
                                        <Box sx={{ 
                                            overflowY: 'auto',
                                            overflowX: 'hidden',
                                            flex: 1,
                                            '&::-webkit-scrollbar': {
                                                width: '8px'
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: alpha(coreColor, 0.05)
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: alpha(coreColor, 0.3),
                                                borderRadius: '4px',
                                                '&:hover': {
                                                    backgroundColor: alpha(coreColor, 0.4)
                                                }
                                            }
                                        }}>
                                            {Object.entries(formData.dailyAllowances).map(([date, allowances], rowIndex) => (
                                                <Box 
                                                    key={date}
                                                    sx={{ 
                                                        display: 'flex',
                                                        borderBottom: rowIndex !== Object.entries(formData.dailyAllowances).length - 1 
                                                            ? `1px solid ${alpha(coreColor, 0.1)}`
                                                            : 'none',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            bgcolor: alpha(coreColor, 0.02)
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ 
                                                        width: '200px',
                                                        p: 2,
                                                        borderRight: `1px solid ${alpha(coreColor, 0.1)}`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        position: 'sticky',
                                                        left: 0,
                                                        bgcolor: 'white',
                                                        zIndex: 1
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2
                                                        }}>
                                                            <Box sx={{
                                                                minWidth: 40,
                                                                height: 40,
                                                                borderRadius: '12px',
                                                                bgcolor: alpha(coreColor, 0.1),
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: coreColor,
                                                                fontWeight: 600
                                                            }}>
                                                                {new Date(date).getDate()}
                                                            </Box>
                                                            <Box>
                                                                <Typography 
                                                                    variant="subtitle2" 
                                                                    sx={{ 
                                                                        color: coreColor,
                                                                        fontWeight: 600,
                                                                        lineHeight: 1.2
                                                                    }}
                                                                >
                                                                    {new Date(date).toLocaleDateString('en-US', { 
                                                                        weekday: 'short'
                                                                    })}
                                                                </Typography>
                                                                <Typography 
                                                                    variant="caption"
                                                                    sx={{ 
                                                                        color: mediumCoreColor,
                                                                        display: 'block'
                                                                    }}
                                                                >
                                                                    {new Date(date).toLocaleDateString('en-US', { 
                                                                        month: 'short',
                                                                        year: 'numeric'
                                                                    })}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ 
                                                        display: 'flex',
                                                        flex: 1,
                                                        overflowX: 'auto',
                                                        '&::-webkit-scrollbar': { display: 'none' }
                                                    }}>
                                                        {Object.entries(allowances).map(([type, value], colIndex) => {
                                                            // Skip these fields in the preview
                                                            if (['provinceId', 'districtId', 'venueId', 'venueType'].includes(type)) {
                                                                return null;
                                                            }
                                                            return (
                                                                <Box 
                                                                    key={type}
                                                                    sx={{ 
                                                                        minWidth: type === 'venue' ? '250px' : '150px',
                                                                        p: 2,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        borderRight: `1px solid ${alpha(coreColor, 0.1)}`
                                                                    }}
                                                                >
                                                                    {type === 'venue' ? (
                                                                        <Typography variant="body2" sx={{ color: coreColor }}>
                                                                            {value || '-'}
                                                                        </Typography>
                                                                    ) : type === 'miscAmount' ? (
                                                                        <Typography variant="body2" sx={{ color: coreColor }}>
                                                                            ${parseFloat(value || 0).toFixed(2)}
                                                                        </Typography>
                                                                    ) : (
                                                                        <Box sx={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            borderRadius: '50%',
                                                                            width: 36,
                                                                            height: 36,
                                                                            bgcolor: value ? alpha(coreColor, 0.1) : 'transparent',
                                                                            border: `2px solid ${value ? coreColor : alpha(coreColor, 0.2)}`,
                                                                            color: value ? coreColor : mediumCoreColor,
                                                                        }}>
                                                                            {value ? <Check sx={{ fontSize: 20 }} /> : <Clear sx={{ fontSize: 20 }} />}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ 
                p: 4, 
                borderTop: `1px solid ${alpha(coreColor, 0.1)}`,
                bgcolor: 'white'
            }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined" 
                    sx={{
                        borderColor: alpha(coreColor, 0.5),
                        color: coreColor,
                        '&:hover': {
                            borderColor: coreColor,
                            backgroundColor: alpha(coreColor, 0.05)
                        }
                    }}
                    startIcon={<ArrowBack />}
                >
                    Back to Edit
                </Button>
                <Button
                    onClick={onSubmit}
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                        bgcolor: coreColor,
                        '&:hover': {
                            bgcolor: alpha(coreColor, 0.9)
                        }
                    }}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                >
                    {isSubmitting ? 'Submitting...' : 'Confirm & Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const TravelRequestForm = memo(({ open, handleClose, selectedPlan }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { createRequest, loading: submitLoading } = useTravelRequest();
    const { listBudgetLineCodes, budgetLines } = useBudgetLine();
    const {
        searchResults,
        selectedTravellers,
        loading: searchLoading,
        error: searchError,
        searchTravellers,
        handleTravellerSelection
    } = useTravellerSearch();

    const [budgetLineData, setBudgetLineData] = useState([]);
    const [uniqueBranchCodes, setUniqueBranchCodes] = useState([]);
    const [filteredProjectCodes, setFilteredProjectCodes] = useState([]);
    const [filteredBudgetLines, setFilteredBudgetLines] = useState([]);
    const [files, setFiles] = useState([]);
    const [fileError, setFileError] = useState('');
    const [formTouched, setFormTouched] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    // File Upload Handlers
    const onDrop = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                const errors = rejectedFiles.map((file) => {
                    const errorMessages = [];
                    if (file.file.size > MAX_FILE_SIZE) {
                        errorMessages.push(`${file.file.name} is too large (max 5MB)`);
                    }
                    if (!ACCEPTED_FILE_TYPES[file.file.type]) {
                        errorMessages.push(`${file.file.name} has invalid file type`);
                    }
                    return errorMessages.join(', ');
                });
                setFileError(`Upload failed: ${errors.join('; ')}`);
                return;
            }

            // Filter out Word documents
            const filteredFiles = acceptedFiles.filter((file) => {
                const fileName = file.name.toLowerCase();
                const isWordDocument = fileName.endsWith('.doc') || fileName.endsWith('.docx') || fileName.endsWith('.odt');
                if (isWordDocument) {
                    setFileError(`${file.name} is not allowed. Word documents (.doc, .docx, .odt) are not supported.`);
                    return false;
                }
                return true;
            });

            if (filteredFiles.length === 0) {
                return;
            }

            const newFiles = filteredFiles.filter((file) => {
                const isDuplicate = files.some((existingFile) => existingFile.name === file.name && existingFile.size === file.size);
                return !isDuplicate;
            });

            if (newFiles.length > 0) {
                setFiles((prevFiles) => [...prevFiles, ...newFiles]);
                setFileError('');
            } else {
                setFileError('Selected files are already attached');
            }
        },
        [files]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ACCEPTED_FILE_TYPES,
        maxSize: MAX_FILE_SIZE,
        multiple: true,
        onDropRejected: (rejectedFiles) => {
            const wordDocErrors = rejectedFiles.filter((file) => {
                const fileName = file.file.name.toLowerCase();
                return fileName.endsWith('.doc') || fileName.endsWith('.docx') || fileName.endsWith('.odt');
            });

            if (wordDocErrors.length > 0) {
                setFileError('Word documents (.doc, .docx, .odt) are not allowed. Please use PDF, JPG, or PNG files only.');
            }
        }
    });

    // File Management Handlers
    const handleRemoveFile = (indexToRemove) => {
        setFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
        setFileError('');
    };

    const handleRemoveAllFiles = async () => {
        const result = await Swal.fire({
            title: 'Remove all files?',
            text: 'Are you sure you want to remove all attached files?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: theme.palette.primary.main,
            cancelButtonColor: theme.palette.error.main,
            confirmButtonText: 'Yes, remove all'
        });

        if (result.isConfirmed) {
            setFiles([]);
            setFileError('');
        }
    };

    // Budget Line and Code Handlers
    const handleBranchCodeChange = (branchId, setFieldValue) => {
        const projectCodes = budgetLineData
            .filter((line) => line.projectCode?.branchCode?.id === branchId)
            .reduce((acc, line) => {
                const project = line.projectCode;
                if (!acc.some((p) => p.id === project.id)) {
                    acc.push({
                        id: project.id,
                        code: project.code,
                        name: project.name,
                        description: project.description
                    });
                }
                return acc;
            }, []);

        setFilteredProjectCodes(projectCodes);
        setFieldValue('projectCode', '');
        setFieldValue('budgetLine', '');
        setFilteredBudgetLines([]);
    };

    const handleProjectCodeChange = (projectId, setFieldValue) => {
        const filteredLines = budgetLineData
            .filter((line) => line.projectCode?.id === projectId)
            .map((line) => ({
                id: line.id,
                code: line.code,
                name: line.name,
                description: line.description,
                currentBalance: line.currentBalance
            }));

        setFilteredBudgetLines(filteredLines);
        setFieldValue('budgetLine', '');
    };

    // Form Submission Handler
    const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
        if (!showPreview) {
            setPreviewData(values);
            setShowPreview(true);
            setSubmitting(false);
            return;
        }

        try {
            setSubmitError('');
            const formData = new FormData();

         

            const submitData = {
                travelPlanId: selectedPlan?.id,
                userIds: values.userIds.filter(Boolean),
                budgetLine: parseInt(values.budgetLine, 10),
                travelType: values.travelType,
                branchCode: values.branchCode,
                projectCode: values.projectCode,
                departureDatetime: formatDateTimeForSubmission(values.departureDatetime),
                returnDatetime: formatDateTimeForSubmission(values.returnDatetime),
                documentTypes: files.length > 0 ? files.map(() => 'JUSTIFICATION') : []
            };

            // Add adjustDayToDay and dailyAllowances if conditions are met
            const hasDailyAllowances = values.dailyAllowances && typeof values.dailyAllowances === 'object' && Object.keys(values.dailyAllowances).length > 0;
            
            if (values.adjustDayToDay && hasDailyAllowances) {
                submitData.adjustDayToDay = true;
                submitData.dailyAllowances = values.dailyAllowances;
           
            } 


            formData.append('travelRequest', new Blob([JSON.stringify(submitData)], { type: 'application/json' }));

            if (files.length > 0) {
                files.forEach((file) => formData.append('files', file));
            }

            await createRequest(formData);

            await Swal.fire({
                title: 'Success!',
                text: 'Your travel request has been submitted successfully.',
                icon: 'success',
                confirmButtonColor: theme.palette.primary.main
            });

            resetForm();
            setFiles([]);
            setShowPreview(false);
            handleClose();
        } catch (error) {
            logger.error('Error submitting travel request:', error);
            
            // Handle date overlap error
            if (error.response?.data?.message?.includes('already have a travel request') && 
                error.response?.data?.message?.includes('overlaps with these dates')) {
                const errorMessage = error.response.data.message;
                setSubmitError(errorMessage);
                
                await Swal.fire({
                    title: 'Date Conflict',
                    html: `
                        <div style="text-align: left;">
                            <p style="margin-bottom: 10px; color: #d32f2f;">
                                <strong>Travel Date Conflict</strong>
                            </p>
                            <p style="margin-bottom: 10px;">
                                ${errorMessage}
                            </p>
                            <p style="font-size: 0.9em; color: #666;">
                                Please adjust your travel dates or wait until the existing trip is completed.
                            </p>
                        </div>
                    `,
                    icon: 'warning',
                    confirmButtonColor: theme.palette.primary.main
                });
            } else {
                setSubmitError('Failed to submit travel request. Please try again.');
                await Swal.fire({
                    title: 'Error',
                    text: error.message,
                    icon: 'error',
                    confirmButtonColor: theme.palette.primary.main
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Effects
    useEffect(() => {
        const fetchBudgetLines = async () => {
            try {
                await listBudgetLineCodes(0, 5000);
            } catch (error) {
                logger.error('Error fetching budget lines:', error);
                setSubmitError('Failed to load budget lines. Please refresh the page.');
            }
        };
        fetchBudgetLines();
    }, [listBudgetLineCodes]);

    useEffect(() => {
        if (budgetLines?.length > 0) {
            setBudgetLineData(budgetLines);
            const uniqueBranches = budgetLines.reduce((acc, line) => {
                const branchCode = line.projectCode?.branchCode;
                if (branchCode && !acc.some((b) => b.id === branchCode.id)) {
                    acc.push({
                        id: branchCode.id,
                        code: branchCode.code,
                        name: branchCode.name,
                        description: branchCode.description
                    });
                }
                return acc;
            }, []);
            setUniqueBranchCodes(uniqueBranches);
        }
    }, [budgetLines]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    minHeight: '90vh',
                    maxHeight: '95vh',
                    minWidth: '95vw'
                }
            }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Create Travel Request</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <DeleteIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Formik
                initialValues={{
                    travelPlanId: selectedPlan?.id || '',
                    userIds: Array(1).fill(''),
                    budgetLine: '',
                    travelType: selectedPlan?.type || '',
                    branchCode: '',
                    projectCode: '',
                    departureDatetime: formatDateTimeForInput(selectedPlan?.departureTime) || '',
                    returnDatetime: formatDateTimeForInput(selectedPlan?.returnTime) || '',
                    travellersCount: 1,
                    adjustDayToDay: false,
                    dailyAllowances: {}
                }}
                validationSchema={createTravelRequestSchema}
                onSubmit={handleFormSubmit}
            >
                {(formik) => {
                    // Calculate dateRange whenever departure or return dates change
                    const dateRange = React.useMemo(() => {
                        if (formik.values.departureDatetime && formik.values.returnDatetime) {
                            const departureDate = formik.values.departureDatetime.split('T')[0];
                            const returnDate = formik.values.returnDatetime.split('T')[0];
                            
                            const dates = [];
                            let currentDate = new Date(departureDate);
                            const lastDate = new Date(returnDate);

                            while (currentDate <= lastDate) {
                                dates.push(currentDate.toISOString().split('T')[0]);
                                currentDate.setDate(currentDate.getDate() + 1);
                            }
                            return dates;
                        }
                        return [];
                    }, [formik.values.departureDatetime, formik.values.returnDatetime]);

                    return (
                        <>
                            <Form onChange={() => setFormTouched(true)}>
                                <Box sx={{ p: 3 }}>
                                    {submitError && (
                                        <Alert severity="error" onClose={() => setSubmitError('')} sx={{ mb: 2 }}>
                                            {submitError}
                                        </Alert>
                                    )}

                                    <Grid container spacing={3}>
                                        <TripDetailsSection formik={formik} selectedPlan={selectedPlan} isMobile={isMobile} />

                                        <TravelerSelectionSection
                                            formik={formik}
                                            selectedTravellers={selectedTravellers}
                                            searchResults={searchResults}
                                            searchLoading={searchLoading}
                                            searchError={searchError}
                                            handleTravellerSelection={handleTravellerSelection}
                                            searchTravellers={searchTravellers}
                                        />

                                        <BudgetCodesSection
                                            formik={formik}
                                            uniqueBranchCodes={uniqueBranchCodes}
                                            filteredProjectCodes={filteredProjectCodes}
                                            filteredBudgetLines={filteredBudgetLines}
                                            handleBranchCodeChange={handleBranchCodeChange}
                                            handleProjectCodeChange={handleProjectCodeChange}
                                        />

                                        {/* Switch for day to day activities */}
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formik.values.adjustDayToDay}
                                                        onChange={(e) => {
                                                            const isChecked = e.target.checked;
                                                            formik.setFieldValue('adjustDayToDay', isChecked);
                                                            
                                                            // Initialize daily allowances when switch is turned on
                                                            if (isChecked && dateRange.length > 0) {
                                                                const initialAllowances = {};
                                                                dateRange.forEach(date => {
                                                                    initialAllowances[date] = {
                                                                        'Breakfast': false,
                                                                        'Lunch': false,
                                                                        'Dinner': false,
                                                                        'Incidental': false,
                                                                        'Proved Acc': false,
                                                                        'Unproved Acc': false,
                                                                        'No Acc Required': false,
                                                                        'miscAmount': '0.00',
                                                                        'venue': ''
                                                                    };
                                                                });
                                                                formik.setFieldValue('dailyAllowances', initialAllowances);
                                                            } else {
                                                                // Clear daily allowances when switch is turned off
                                                                formik.setFieldValue('dailyAllowances', {});
                                                            }
                                                        }}
                                                        name="adjustDayToDay"
                                                        color="primary"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="subtitle1" color="textPrimary">
                                                        Adjust day to day activities
                                                    </Typography>
                                                }
                                            />
                                        </Grid>

                                        {/* Daily Allowance Matrix */}
                                        {formik.values.adjustDayToDay && dateRange.length > 0 && (
                                            <Grid item xs={12}>
                                                <DailyAllowanceMatrix
                                                    dates={dateRange}
                                                    formik={formik}
                                                />
                                            </Grid>
                                        )}

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Supporting Documents (Optional)
                                            </Typography>
                                            <FileUploadSection
                                                files={files}
                                                onRemoveFile={handleRemoveFile}
                                                onRemoveAllFiles={handleRemoveAllFiles}
                                                getRootProps={getRootProps}
                                                getInputProps={getInputProps}
                                                isDragActive={isDragActive}
                                                fileError={fileError}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box sx={{ 
                                    borderTop: 1, 
                                    borderColor: 'divider',
                                    p: 2.5, 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <Button onClick={handleClose} variant="outlined" color="error">
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={(!formik.isValid && formTouched) || formik.isSubmitting}
                                        startIcon={formik.isSubmitting ? <CircularProgress size={20} /> : null}
                                    >
                                        {formik.isSubmitting ? 'Processing...' : 'Preview Request'}
                                    </Button>
                                </Box>
                            </Form>

                            <PreviewDialog
                                open={showPreview}
                                onClose={() => setShowPreview(false)}
                                formData={previewData}
                                files={files}
                                onSubmit={() => formik.handleSubmit()}
                                isSubmitting={formik.isSubmitting}
                            />
                        </>
                    );
                }}
            </Formik>
        </Dialog>
    );
});

TravelRequestForm.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    selectedPlan: PropTypes.object
};

export default TravelRequestForm; 